import { useState } from 'react'
import uniq from 'lodash/uniq'
import { Dialog, DialogContent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DialogTitleClosable, type GridColDef, Pagination } from '@dhl/components'
import { type BillingUnitDto, useAssignBillingUnitsToUser } from '@dhl/mdp-api'
import { Box, Button, Divider, H5, Paper } from '@dhl-ui/components'
import { AdminDataGrid, SearchTextField, useNotificationMessage } from '~/common'
import { useBillingUnits } from '~/modules/billingunits/hooks'
import { type AdminBillingUnit } from '~/modules/adminusers/model'

type Props = {
  adminUserId: string
  adminBillingUnits: AdminBillingUnit[]
  onClose: () => void
  onRefetch: () => void
}

export const AdminBillingUnitsAssignDialog = ({ adminUserId, adminBillingUnits, onClose, onRefetch }: Props) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const { showError, showSuccess } = useNotificationMessage()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const { billingUnits, isLoading } = useBillingUnits()
  const { mutate: assignBillingUnitsToUser } = useAssignBillingUnitsToUser()

  // Apply search
  const filteredBillingUnits = search
    ? billingUnits.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()) || b.cccId.includes(search))
    : billingUnits

  // Make already assigned billing units unselectable
  const selectableBillingUnits = filteredBillingUnits.filter((b) => !adminBillingUnits.map((b) => b.id).includes(b.id!))

  const addBillingUnits = () => {
    const newBillingUnits = uniq(adminBillingUnits.map((b) => b.id).concat(selectedIds))
    assignBillingUnitsToUser(
      { adminUserId, data: newBillingUnits },
      {
        onSuccess: () => {
          showSuccess(t('admin.billingUnitsPage.addAdminBillingUnitMessage'))
          onRefetch()
          onClose()
        },
        onError: (response) => {
          showError(response?.message ?? t('admin.errorMessage'))
        },
      }
    )
  }

  const columns: GridColDef<AdminBillingUnit>[] = [
    {
      field: 'id',
    },
    {
      field: 'name',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.name'),
      flex: 1,
    },
    {
      field: 'cccId',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.identifier'),
      flex: 1,
    },
  ]

  return (
    <Dialog data-testid="existing-billing-units-dialog" open={true} onClose={onClose} fullWidth>
      <Box>
        <DialogTitleClosable onClose={onClose}>
          <H5>{t('admin.billingUnitsPage.billingUnitsOverview.title')}</H5>
        </DialogTitleClosable>
      </Box>
      <Divider />
      <DialogContent>
        <Box pt={2}>
          <SearchTextField onChange={setSearch} withoutSearchParams />
          <Box mt={2}>
            <Paper>
              <AdminDataGrid<BillingUnitDto>
                initialState={{
                  columns: { columnVisibilityModel: { id: false } },
                  sorting: { sortModel: [{ field: 'cccId', sort: 'asc' }] },
                }}
                onSortModelChange={() => {
                  setPage(0)
                }}
                loading={isLoading}
                data-testid="admin-billing-units-assign-table"
                autoHeight
                rows={selectableBillingUnits}
                columns={columns}
                paginationModel={{
                  page,
                  pageSize,
                }}
                checkboxSelection={true}
                onRowSelectionModelChange={(newSelectionModel) => {
                  setSelectedIds(newSelectionModel as string[])
                }}
                rowHeight={48}
                columnHeaderHeight={48}
                disableRowSelectionOnClick
                disableColumnFilter
                hideFooter
              />
            </Paper>
            <Box mt={2}>
              <Pagination
                page={page}
                count={billingUnits.length}
                onPageChange={(_, page) => setPage(page)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={setPageSize}
              />
            </Box>
          </Box>
          <Box marginTop={2} marginBottom={2} display="flex" justifyContent="flex-end">
            <Box marginRight={1}>
              <Button color="primary" onClick={onClose}>
                {t('admin.organisationsPage.users.cancel')}
              </Button>
            </Box>
            <Button
              size="medium"
              color="primary"
              type="submit"
              variant="contained"
              onClick={addBillingUnits}
              disabled={selectedIds.length === 0}
            >
              {t('admin.billingUnitsPage.billingUnitsOverview.addBillingunitButtonTitle')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
